import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Pagination } from "../../components/common/UI";
import {
  ShieldAlert,
  Search,
  RefreshCw,
  Clock,
  User,
  Mail,
  Phone,
  FileCheck,
  Loader2,
  Inbox,
  ChevronDown,
  ShieldCheck,
  X
} from "lucide-react";

// Glassmorphism card shell styling matching rest of the dashboard pages
const containerShell =
  "relative bg-white/60 backdrop-blur-xl rounded-2xl border border-white/50 shadow-lg ring-1 ring-slate-900/[0.04] p-6 overflow-hidden transition-all duration-300";

// Format date to: "DD MMM YYYY, h:mm a"
function formatUserDate(dateStr) {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return dateStr;
  }
}

const DSAR_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses", color: "bg-indigo-500", text: "text-indigo-700", bg: "bg-indigo-50/90 border-indigo-200/90 hover:bg-indigo-100/80" },
  { value: "open", label: "Open", color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50/90 border-amber-200/90 hover:bg-amber-100/80" },
  { value: "pending", label: "Pending", color: "bg-orange-500", text: "text-orange-700", bg: "bg-orange-50/90 border-orange-200/90 hover:bg-orange-100/80" },
  { value: "in_process", label: "In Process", color: "bg-sky-500", text: "text-sky-700", bg: "bg-sky-50/90 border-sky-200/90 hover:bg-sky-100/80" },
  { value: "approved", label: "Approved", color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50/90 border-emerald-200/90 hover:bg-emerald-100/80" },
  { value: "rejected", label: "Rejected", color: "bg-rose-500", text: "text-rose-700", bg: "bg-rose-50/90 border-rose-200/90 hover:bg-rose-100/80" },
  { value: "delete", label: "Delete", color: "bg-purple-500", text: "text-purple-700", bg: "bg-purple-50/90 border-purple-200/90 hover:bg-purple-100/80" },
];

function DSARStatusFilterDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentOption = DSAR_STATUS_OPTIONS.find((opt) => opt.value === value) || DSAR_STATUS_OPTIONS[0];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold shadow-sm transition-all duration-200 hover:shadow ${currentOption.bg} ${currentOption.text}`}
      >
        <span className="text-[10px] uppercase tracking-wider font-extrabold opacity-60">Status:</span>
        <span className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${currentOption.color} animate-pulse`} />
          {currentOption.label}
        </span>
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1.5 w-48 bg-white/95 backdrop-blur-xl rounded-xl border border-slate-200 shadow-xl ring-1 ring-slate-900/5 z-50 p-1.5 space-y-1"
          >
            {DSAR_STATUS_OPTIONS.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    isSelected
                      ? `${opt.bg} ${opt.text}`
                      : "text-slate-700 hover:bg-slate-100/70 hover:text-slate-900"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${opt.color}`} />
                    {opt.label}
                  </span>
                  {isSelected && <ShieldCheck size={13} className={opt.text} />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Helper for status badge colors and dot indicators
function getStatusBadgeMeta(statusStr) {
  const clean = (statusStr || "").toLowerCase().trim().replace(/\s+/g, "_");
  if (clean === "approved" || clean === "processed" || clean === "closed" || clean === "completed" || clean === "resolved" || clean === "success") {
    return { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" };
  }
  if (clean === "rejected" || clean === "failed" || clean === "denied") {
    return { cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" };
  }
  if (clean === "delete" || clean === "deleted" || clean === "erasure") {
    return { cls: "bg-purple-50 text-purple-700 border-purple-200", dot: "bg-purple-500" };
  }
  if (clean === "in_process" || clean === "inprocess" || clean === "processing") {
    return { cls: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500" };
  }
  if (clean === "pending") {
    return { cls: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" };
  }
  if (clean === "open" || clean === "active") {
    return { cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" };
  }
  return { cls: "bg-slate-50 text-slate-700 border-slate-200", dot: "bg-slate-400" };
}

function formatUserFriendlyLogMessage(logs) {
  if (!logs) {
    return {
      title: "Consent Service Forwarding",
      statusText: "Pending Sync",
      statusClass: "bg-amber-50 text-amber-700 border-amber-200",
      dot: "bg-amber-500 animate-pulse",
      description: "Request forwarding to the consent system is currently pending or queued.",
    };
  }

  if (logs.success || logs.status_code === 200) {
    let msg = "DSAR request forwarded and synced with consent system successfully.";
    if (logs.response && typeof logs.response === "object" && logs.response.message) {
      msg = logs.response.message;
    }
    return {
      title: "Consent Service Forwarding",
      statusText: "Synced Successfully",
      statusClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
      description: msg,
    };
  }

  // Handle errors in a user-friendly manner
  let errDesc = "Failed to sync request with external consent system.";
  const rawErr = String(logs.error || logs.response || "").toLowerCase();

  if (logs.status_code === 405 || rawErr.includes("method not allowed")) {
    errDesc = "Consent deletion API endpoint is not receiving POST requests (HTTP 405 Method Not Allowed). Please check external API URL configuration.";
  } else if (logs.status_code === 404 || rawErr.includes("not found")) {
    errDesc = "Consent deletion API endpoint URL was not found on the external server.";
  } else if (logs.status_code === 500 || rawErr.includes("internal server error")) {
    errDesc = "External consent service encountered an internal processing error.";
  } else if (rawErr.includes("connect") || rawErr.includes("timeout")) {
    errDesc = "Network connection timeout while contacting external consent service.";
  } else if (typeof logs.response === "object" && logs.response?.message) {
    errDesc = logs.response.message;
  } else if (typeof logs.response === "string" && logs.response.trim()) {
    errDesc = logs.response;
  }

  return {
    title: "Consent Service Forwarding",
    statusText: logs.status_code ? `Sync Failed (${logs.status_code})` : "Sync Failed",
    statusClass: "bg-rose-50 text-rose-700 border-rose-200",
    dot: "bg-rose-500",
    description: errDesc,
  };
}

function DSARRecordLogsSlider({ record, onClose }) {
  if (!record) return null;

  const statusMeta = getStatusBadgeMeta(record.status);
  const logs = record.logs || null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        />

        {/* Slide-over panel */}
        <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 220 }}
            className="w-screen max-w-full md:max-w-[50vw] bg-white shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-200/80 bg-slate-50/70 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <FileCheck size={20} className="text-indigo-500" />
                  <h2 className="text-base font-bold text-slate-900 font-display">
                    DSAR Request Audit & Logs
                  </h2>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-200/80 px-2 py-0.5 rounded-lg">
                    {record.request_id}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-xl text-xs font-bold border ${statusMeta.cls}`}>
                    <span className={`w-2 h-2 rounded-full ${statusMeta.dot}`} />
                    {record.status}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/60 transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto no-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden p-6 space-y-6">
              {/* Subject Info Card */}
              <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-3">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  Data Subject Details
                </p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[11px]">Name</span>
                    <span className="font-bold text-slate-800 flex items-center gap-1.5 mt-0.5">
                      <User size={13} className="text-sky-500" />
                      {record.name}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Data Owner Email</span>
                    <span className="font-bold text-slate-800 flex items-center gap-1.5 mt-0.5 truncate" title={record.data_owner}>
                      <Mail size={13} className="text-indigo-500" />
                      {record.data_owner}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Phone</span>
                    <span className="font-bold text-slate-800 flex items-center gap-1.5 mt-0.5">
                      <Phone size={13} className="text-emerald-500" />
                      {record.phone || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Request Type</span>
                    <span className="font-bold text-slate-800 flex items-center gap-1.5 mt-0.5">
                      <FileCheck size={13} className="text-purple-500" />
                      {record.request_type}
                    </span>
                  </div>
                </div>
              </div>

              {/* LOG TIMELINE */}
              <div className="space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <Clock size={14} className="text-indigo-500" />
                  Activity & Execution Logs
                </h3>

                {/* Log 1: DSAR Request Received */}
                <div className="border border-slate-200/90 rounded-2xl p-4 bg-white shadow-sm space-y-3 relative">
                  <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-xs font-bold text-slate-800">
                        DSAR Request Received
                      </span>
                    </div>
                    <span className="text-[11px] font-semibold text-slate-400 tabular-nums">
                      {formatUserDate(record.created_at)}
                    </span>
                  </div>

                  {record.deleted_total !== null && record.deleted_total !== undefined ? (
                    <div className="p-2.5 bg-rose-50/60 border border-rose-100 rounded-xl text-xs text-rose-800 font-medium">
                      <span className="font-bold">Total Fields Deleted:</span> {record.deleted_total}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 font-medium">
                      Request logged successfully.
                    </p>
                  )}
                </div>

                {/* Log 2: CONSENT_DSAR_DELETE Forwarding Status */}
                {(() => {
                  const logMeta = formatUserFriendlyLogMessage(logs);
                  return (
                    <div className="border border-slate-200/90 rounded-2xl p-4 bg-white shadow-sm space-y-3 relative">
                      <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${logMeta.dot}`} />
                          <span className="text-xs font-bold text-slate-800">
                            {logMeta.title}
                          </span>
                        </div>
                        <span className="text-[11px] font-semibold text-slate-400 tabular-nums">
                          {logs?.timestamp ? formatUserDate(logs.timestamp) : "Pending"}
                        </span>
                      </div>

                      <div className="space-y-2.5 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-400 font-medium">Forwarding Status</span>
                          <span className={`px-2.5 py-0.5 rounded-xl font-bold text-[11px] border ${logMeta.statusClass}`}>
                            {logMeta.statusText}
                          </span>
                        </div>

                        <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl text-slate-700 font-medium leading-relaxed">
                          {logMeta.description}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}

export default function DSARRecords() {
  const { showNotification } = useApp();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRecordForLogs, setSelectedRecordForLogs] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const fetchRecords = async (showToast = false) => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/dsar_records");
      if (!res.ok) {
        throw new Error(`Failed to load DSAR records: ${res.statusText}`);
      }
      const data = await res.json();
      setRecords(data);
      if (showToast) {
        showNotification("DSAR records refreshed successfully", "success");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred while fetching records.");
      showNotification(err.message || "Failed to load DSAR records", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter records based on search and status
  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      if (statusFilter !== "all") {
        const cleanRecStatus = (r.status || "").toLowerCase().trim().replace(/\s+/g, "_");
        const cleanFilter = statusFilter.toLowerCase().trim().replace(/\s+/g, "_");
        let match = cleanRecStatus === cleanFilter;
        if (cleanFilter === "in_process") {
          match = cleanRecStatus === "in_process" || cleanRecStatus === "inprocess" || cleanRecStatus === "processing";
        } else if (cleanFilter === "delete") {
          match = cleanRecStatus === "delete" || cleanRecStatus === "deleted" || cleanRecStatus === "erasure";
        }
        if (!match) return false;
      }

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        return (
          r.request_id?.toLowerCase().includes(term) ||
          r.name?.toLowerCase().includes(term) ||
          r.data_owner?.toLowerCase().includes(term) ||
          r.request_type?.toLowerCase().includes(term) ||
          r.status?.toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [records, searchTerm, statusFilter]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage) || 1;
  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(start, start + itemsPerPage);
  }, [filteredRecords, currentPage]);

  // Reset page on search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    <div className="space-y-6 w-full p-4 md:p-6">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
            <ShieldAlert className="text-indigo-500" size={26} />
            Data Subject Access Requests (DSAR)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Overview of access, correction, and erasure requests submitted by data subjects.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => fetchRecords(true)}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {/* FILTER & CONTAINER */}
      <div className={containerShell}>
        {/* Search bar & Status Filter */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-5">
          <div className="text-xs text-slate-400 font-medium self-start sm:self-center">
            Total records: {filteredRecords.length}
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <DSARStatusFilterDropdown
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search by Request ID, Name, Email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-1.5 text-xs font-medium rounded-xl border border-slate-200/90 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-white/90 shadow-sm"
              />
            </div>
          </div>
        </div>

        {/* LOADING STATE */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="animate-spin text-indigo-500" size={32} />
            <p className="text-sm text-slate-500 font-medium">Loading DSAR records...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <ShieldAlert className="text-red-400 mb-2" size={40} />
            <p className="text-sm font-semibold text-slate-800">Failed to load records</p>
            <p className="text-xs text-slate-500 mt-1 max-w-sm">{error}</p>
            <Button variant="secondary" onClick={() => fetchRecords(true)} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Inbox className="text-slate-300 mb-2" size={48} />
            <p className="text-sm font-medium text-slate-600">No DSAR Records found</p>
            <p className="text-xs text-slate-400 mt-1">
              {searchTerm ? "Try searching with a different keyword." : "Pushed records from external systems will appear here."}
            </p>
          </div>
        ) : (
          /* TABLE SECTION */
          <div className="overflow-x-auto -mx-6">
            <table className="w-full border-collapse text-left min-w-[800px]">
              <thead>
                <tr className="border-b border-slate-200/80 bg-slate-50/70 text-slate-600 text-xs font-semibold uppercase tracking-wider">
                  <th className="py-3.5 px-6 w-16">S.No</th>
                  <th className="py-3.5 px-6">Request ID</th>
                  <th className="py-3.5 px-6">Subject Details</th>
                  <th className="py-3.5 px-6">Request Type</th>
                  <th className="py-3.5 px-6">Status</th>
                  <th className="py-3.5 px-6">Data Deleted</th>
                  <th className="py-3.5 px-6">Submitted At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                {paginatedRecords.map((record, index) => {
                  const sNo = (currentPage - 1) * itemsPerPage + index + 1;
                  return (
                    <tr
                      key={record.id}
                      className="hover:bg-slate-50/40 transition-colors group"
                    >
                      {/* S.No */}
                      <td className="py-4 px-6 text-slate-500 font-medium whitespace-nowrap text-xs w-16">
                        {sNo}
                      </td>

                      {/* Request ID */}
                      <td className="py-4 px-6 font-semibold font-mono tracking-tight text-xs">
                        <button
                          type="button"
                          onClick={() => setSelectedRecordForLogs(record)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors focus:outline-none"
                        >
                          {record.request_id}
                        </button>
                      </td>

                    {/* Subject Details */}
                    <td className="py-4 px-6">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 font-medium text-slate-800">
                          <User size={14} className="text-sky-500" />
                          {record.name}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Mail size={12} className="text-indigo-500" />
                          {record.data_owner}
                        </div>
                        {record.phone && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Phone size={12} className="text-teal-500" />
                            {record.phone}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Request Type */}
                    <td className="py-4 px-6 capitalize">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-50/80 text-indigo-700 border border-indigo-100">
                        <FileCheck size={12} />
                        {record.request_type}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-4 px-6">
                      {(() => {
                        const meta = getStatusBadgeMeta(record.status);
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border ${meta.cls}`}>
                            <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                            {record.status}
                          </span>
                        );
                      })()}
                    </td>

                    {/* Data Deleted */}
                    <td className="py-4 px-6 text-xs">
                      {record.deleted_total !== null && record.deleted_total !== undefined ? (
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-red-600">
                            {record.deleted_total} fields
                          </span>
                          {record.deleted_details && record.deleted_details.length > 0 && (
                            <div className="flex flex-wrap gap-1 text-[10px]">
                              {record.deleted_details.map((item, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-semibold border border-red-100"
                                >
                                  {item.data_type}: {item.deleted}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 font-medium">-</span>
                      )}
                    </td>

                    {/* Submitted At */}
                    <td className="py-4 px-6 text-slate-500 font-medium whitespace-nowrap text-xs">
                      <div className="flex items-center gap-1.5">
                        <Clock size={13} className="text-slate-400" />
                        {formatUserDate(record.created_at)}
                      </div>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION SECTION */}
        {!loading && filteredRecords.length > 0 && (
          <Pagination
            page={currentPage}
            pages={totalPages}
            total={filteredRecords.length}
            pageSize={itemsPerPage}
            onPageChange={setCurrentPage}
            itemLabel="records"
            className="border-t border-slate-100 pt-5 mt-5"
          />
        )}
      </div>

      {/* LOGS SLIDER DRAWER — Page Root Scope */}
      {selectedRecordForLogs && (
        <DSARRecordLogsSlider
          record={selectedRecordForLogs}
          onClose={() => setSelectedRecordForLogs(null)}
        />
      )}
    </div>
  );
}
